import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { LatLng } from 'shared';
import { ReferralInfo } from 'ux/Public/types';

export type OfficeRequestState = {
  referralInfo?: ReferralInfo;
  id?: number;
  market: string | null; // TODO: type this better?
  locations: OfficeRequestLocation[];
  entry?: OfficeRequestEntry;
  headCount?: number;
  maxMonthlyBudget?: number;
  tour?: OfficeRequestTour;
  user: OfficeRequestUser;
  completedSteps: number[];
  jobTitle?: string | null;
  launchPath?: string;
  exitModalIsVisible: boolean;
  tourRequestWorkspaceId?: number;
};

export interface OfficeRequestUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  callingCode: string;
}

export interface OfficeRequestLocation {
  neighborhood?: string;
  city: string;
  region: string;
  country: string;
  place_id: string;
  loc?: LatLng;
}

type OfficeRequestEntry = 'home-page-search' | 'home-page-get-started' | 'location-page-primary-cta';

export type OfficeRequestTour = {
  startsAt: string; // ISO string
  endsAt: string; // ISO string
};

const initialState: OfficeRequestState = {
  market: null,
  locations: [],
  user: { callingCode: '1' },
  completedSteps: [],
  headCount: 10,
  jobTitle: null,
  exitModalIsVisible: false
};

const officeRequestSlice = createSlice({
  name: 'OfficeRequest',
  initialState,
  reducers: {
    setReferralInfo: (state, action: PayloadAction<ReferralInfo>) => {
      state.referralInfo = action.payload;
    },
    setId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
    },
    setMarket: (state, action: PayloadAction<string | null>) => {
      state.market = action.payload;
    },
    clearLocations: state => {
      state.locations = [];
    },
    addLocation: (state, action: PayloadAction<OfficeRequestLocation>) => {
      state.locations.unshift(action.payload);
    },
    removeLocation: (state, action: PayloadAction<OfficeRequestLocation>) => {
      state.locations = state.locations.filter(
        location =>
          location.place_id !== action.payload.place_id ||
          location.neighborhood !== action.payload.neighborhood ||
          location.country !== action.payload.country ||
          location.region !== action.payload.region ||
          location.city !== action.payload.city ||
          location.loc?.lat !== action.payload.loc?.lat ||
          location.loc?.lng !== action.payload.loc?.lng
      );
    },
    setEntry: (state, action: PayloadAction<OfficeRequestEntry>) => {
      state.entry = action.payload;
    },
    setHeadCount: (state, action: PayloadAction<number | undefined>) => {
      state.headCount = action.payload;
    },
    setMaxMonthlyBudget: (state, action: PayloadAction<number | undefined>) => {
      state.maxMonthlyBudget = action.payload;
    },
    setUser: (state, action: PayloadAction<Partial<OfficeRequestUser>>) => {
      state.user = { ...state.user, ...action.payload };
    },
    addStepComplete: (state, action: PayloadAction<number>) => {
      state.completedSteps.push(action.payload);
    },
    setJobTitle: (state, action: PayloadAction<string>) => {
      state.jobTitle = action.payload;
    },
    setLaunchPath: (state, action: PayloadAction<string>) => {
      state.launchPath = action.payload;
    },
    setExitModalIsVisible: (state, action: PayloadAction<boolean>) => {
      state.exitModalIsVisible = action.payload;
    },
    setTourRequestWorkspaceId: (state, action: PayloadAction<number | undefined>) => {
      state.tourRequestWorkspaceId = action.payload;
    },
    setTour: (state, action: PayloadAction<OfficeRequestTour>) => {
      state.tour = action.payload;
    },
    reset: () => initialState
  }
});

export const { actions } = officeRequestSlice;

export default officeRequestSlice.reducer;
